import * as Yup from 'yup';
import { TFunction } from 'i18next';

/**
 * Field-level Yup builders for JobPosition fields.
 *
 * Single source of truth for per-field validation rules. Compose these into
 * step-specific schemas (job-position-onboarding) or any other JP-creating
 * flow (autonomous org onboarding) to keep constraints in sync — when the
 * backend changes a rule, only one place needs updating.
 *
 * Cross-field tests (e.g. salary min/max sanity, accommodation conditional
 * on seasonal flags) stay in the consuming step schema, since they describe
 * step-level composition, not field-level constraints.
 */

/**
 * Range rules for `staff_requested`, without the "required" rule.
 *
 * The value is mandatory on its own step, but not for the job ad as a whole: a
 * draft saved through "save and continue later" before that step is
 * legitimately missing it. Keep the range rules in both cases — an out-of-range
 * value is a bug wherever it comes from, and is what the submit guard exists to
 * stop (AppSignal #1479: `staff_requested` out of int4 range from AI prefill).
 */
export const staffRequestedRangeField = (t: TFunction) =>
  Yup.number()
    .nullable()
    // A cleared number input hands back '', which Yup would otherwise cast to
    // NaN and reject as non-numeric. Treat it as "not answered yet" so the
    // guard lets a half-filled draft through, and so the step asks for the
    // field rather than complaining it is not a number.
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .typeError(t('steps.other_requirements.staff_requested.validation_numeric'))
    .positive(t('steps.other_requirements.staff_requested.validation_positive'))
    .integer(t('steps.other_requirements.staff_requested.validation_integer'))
    .max(999, t('steps.other_requirements.staff_requested.validation_max'));

/** The step-level field: the range rules plus "you must answer this". */
export const staffRequestedField = (t: TFunction) => staffRequestedRangeField(t).required(t('errors.required_field'));

export const advertisedLocationField = (t: TFunction) => Yup.string().required(t('errors.required_field'));

/**
 * Base validator for the worker_max_distance field (radius in km from the venue
 * within which a candidate may apply). The "required" rule is intentionally
 * NOT applied here because requirement depends on `advertised_location`. Step
 * schemas compose `.when('advertised_location', ...)` on top of this builder.
 */
export const workerMaxDistanceField = (t: TFunction) =>
  Yup.number()
    .nullable()
    .typeError(t('errors.required_field'))
    .positive(t('errors.required_field'))
    .integer(t('errors.required_field'));

export const positiveSalaryField = (t: TFunction) =>
  Yup.number()
    .nullable()
    .typeError(t('steps.salary.salary_validation_numeric'))
    .positive(t('steps.salary.salary_validation_positive'));

export const restaurantIdField = (t: TFunction) => Yup.string().required(t('errors.venue_required'));

export const domainInterestField = (t: TFunction) => Yup.string().required(t('errors.domain_interest_required'));

/**
 * Subset of JP fields that any JP-creating flow MUST satisfy before sending
 * the payload to `POST /organizations` or `POST /job_positions`.
 *
 * Use this for last-mile defensive validation in flows where the user may
 * skip individual form steps (e.g. autonomous org onboarding with AI-prefill).
 *
 * Deliberately checks ranges, not presence. A field being mandatory on its own
 * step does not make it mandatory for the ad as a whole: org onboarding buffers
 * the ad and submits it in one call, so "save and continue later" legitimately
 * produces a draft that has not reached every step yet. Requiring presence here
 * made that submit fail before any request was sent.
 */
export const jobPositionSubmitGuardSchema = (t: TFunction) =>
  Yup.object({
    staff_requested: staffRequestedRangeField(t),
  });
